import React from "react"
import { Link } from "gatsby"
import Layout from "../components/Layout"
import Pagetitle from "../components/Pagetitle"
import Seo from "../components/Seo"
import { platforms } from "../data/platforms"

const GettingStartedPage = () => (
  <Layout>
    <Seo
      title="Getting Started"
      description="A step-by-step guide to setting up your Upfeed product feed and connecting it to Pinterest, Meta, Google, TikTok, Microsoft Ads, and more."
    />
    <div className="container">
      <div className="row justify-content-center">
        <div className="getting-started col-md-8">
          <Pagetitle>How to Set Up Your Shopify Product Feed</Pagetitle>
          <p>
            A product feed is a file containing all your product data — titles,
            prices, images, and availability — that advertising platforms use to
            automatically create and target ads. This guide walks you through
            setting up your UpFeed product feed and connecting it to your first
            advertising platform. This takes about 5 to 10 minutes.
          </p>

          <h3 id="generate">
            <span className="itemno">1</span> Generate your product feed
          </h3>
          <p>
            After installing the app, your feed is automatically generated.
            Open the UpFeed app homepage and copy the feed URL — you'll need
            this when connecting to each advertising platform.
          </p>
          <p>
            If you've added or updated products since installing the app,
            the changes will be reflected in the next feed update. 
            Your feed updates 4 times daily, so product changes — inventory, pricing, 
            photos, and new products — are always reflected in your ads.
          </p>
 
          <h3 id="googlecats">
            <span className="itemno">2</span> Set Google Product Categories
          </h3>
          <p>
            Google Product Categories help advertising platforms understand
            exactly what you sell, so your ads are shown to the right audience.
            They are required for Google and Pinterest, and recommended for all
            other platforms.
          </p>
          <p>
            To set categories, click <strong>Set/Edit Google Product
            Categories</strong> on the app homepage, then click{" "}
            <strong>Map Categories</strong>. Match each of your store's product
            categories to the most relevant Google Product Category.
          </p>
          <p>
            Skipping this step may cause products to be rejected by Google or
            Pinterest, or placed into a broad catch-all category that performs
            poorly in ads.
          </p>
 
          <h3 id="settings">
            <span className="itemno">3</span> Configure feed settings{" "}
            <span className="optional-tag">Optional</span>
          </h3>
          <p>
            UpFeed gives you control over what products appear in your feed and
            how they are structured. These settings are optional but can
            significantly improve the quality and relevance of your ads.
          </p>
 
          <h4>Feed behaviour</h4>
          <p>
            Found under the <strong>Settings</strong> tab in your feed. Key
            options include:
          </p>
          <ul>
            <li>
              <strong>Exclude out-of-stock products</strong> — removes
              unavailable products from your feed so you never pay to advertise
              items you can't sell.
            </li>
            <li>
              <strong>Include variants</strong> — when enabled, each product
              variant (e.g. size or colour) is listed as a separate item in
              your feed. Recommended for apparel and footwear, as platforms like
              Google and Meta can match ads more precisely to what a shopper is
              searching for.
            </li>
          </ul>
 
          <h4>Exclusions</h4>
          <p>
            Also in the <strong>Settings</strong> tab, you can exclude specific
            products from your feed without removing them from your store:
          </p>
          <ul>
            <li>
              <strong>Exclude by tag</strong> — add a tag like{" "}
              <code>no-ads</code> or <code>internal</code> to any product in
              Shopify and enter that tag here to keep it out of your feed.
            </li>
            <li>
              <strong>Exclude by product ID or variant ID</strong> — enter
              specific Shopify IDs (comma-separated) to exclude individual
              products or variants.
            </li>
            <li>
              <strong>Featured product photo</strong> — choose which product
              image number appears in your ads (e.g. enter{" "}
              <code>2</code> to use the second product photo). Useful if your
              first photo is a lifestyle image but you want a clean white
              background image for shopping ads.
            </li>
          </ul>
 
          <h4>Column mapping</h4>
          <p>
            Under the <strong>Mapping</strong> tab you can customise which
            Shopify fields map to each feed attribute. The required fields —
            id, title, description, price, availability, brand, image_link,
            and link — are pre-mapped automatically. You can also:
          </p>
          <ul>
            <li>
              Add custom attributes using <strong>Add New Attribute</strong> for
              platforms that support additional fields.
            </li>
            <li>
              Reorder columns by dragging rows — useful if a platform expects
              specific fields near the top of the feed.
            </li>
            <li>
              Remove optional columns you don't need to keep your feed lean and
              reduce the chance of validation errors.
            </li>
          </ul>

                    <h3 id="platforms">
            <span className="itemno">4</span> Connect to an advertising platform
          </h3>
          <p>
            Select your advertising platform below for step-by-step
            instructions on adding your feed URL:
          </p>
          <p>
            <strong>Not sure where to start?</strong> If you're new to product
            feed advertising, begin with{" "}
            <Link to="/add-product-feed/google/">Google Merchant Center</Link>{" "}
            — it has the largest reach and your feed will also power free
            product listings across Google Search.
          </p>
          <p>
            Note that most platforms take <strong>24–48 hours</strong> to
            review and approve products after your feed is connected. Microsoft
            Ads can take up to 3 weeks for initial review. Your ads won't serve
            until products are approved, so it's worth connecting your feed as
            early as possible.
          </p>

          <div className="platform-grid">
            {platforms.map(platform => (
              <Link
                key={platform.slug}
                to={`/add-product-feed/${platform.slug}/`}
                className="platform-card"
              >
                <div className="platform-name">{platform.name}</div>
                <p className="platform-desc">{platform.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Layout>
)

export default GettingStartedPage
